import React, { useEffect, useState } from "react";
import {
  Drawer,
  Descriptions,
  Tag,
  Typography,
  Divider,
  List,
  Space,
  Button,
  Upload,
  Popconfirm,
  Spin,
  Empty,
} from "antd";
import {
  PaperClipOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  DOCUMENT_AND_IMAGE_ACCEPT,
  beforeUploadDocumentOrImage,
  filterDocumentOrImageFileList,
} from "../../../utils/fileUpload";

const { Title, Text } = Typography;

const HolidayPackageDetailDrawer = ({
  visible,
  onClose,
  packageId,
  onFetchDetail,
  onDeleteFile,
  onUploadFiles,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [newFileList, setNewFileList] = useState([]);

  useEffect(() => {
    if (visible && packageId) {
      loadDetail();
    }
    if (!visible) {
      setDetail(null);
      setNewFileList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, packageId]);

  const loadDetail = async () => {
    setLoading(true);
    const data = await onFetchDetail(packageId);
    setDetail(data);
    setLoading(false);
  };

  const handleDeleteFile = async (fileId) => {
    setDeletingFileId(fileId);
    const ok = await onDeleteFile(packageId, fileId);
    if (ok) {
      setDetail((prev) => ({
        ...prev,
        files: prev.files.filter((f) => f.id !== fileId),
      }));
    }
    setDeletingFileId(null);
  };

  const handleUploadMore = async () => {
    if (!newFileList.length) return;
    setUploadingFiles(true);
    const rawFiles = newFileList.map((f) => f.originFileObj || f);
    const uploaded = await onUploadFiles(packageId, rawFiles);
    if (uploaded) {
      setDetail((prev) => ({
        ...prev,
        files: [...(prev.files || []), ...uploaded],
      }));
      setNewFileList([]);
    }
    setUploadingFiles(false);
  };

  const uploadProps = {
    beforeUpload: beforeUploadDocumentOrImage,
    multiple: true,
    fileList: newFileList,
    onChange: ({ fileList }) =>
      setNewFileList(filterDocumentOrImageFileList(fileList)),
    accept: DOCUMENT_AND_IMAGE_ACCEPT,
  };

  const renderContent = () => {
    if (loading)
      return (
        <Spin
          style={{ display: "block", textAlign: "center", marginTop: 40 }}
        />
      );
    if (!detail) return <Empty description="No details available" />;

    return (
      <>
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="Title">{detail.title}</Descriptions.Item>
          <Descriptions.Item label="Class">
            {detail.className}
          </Descriptions.Item>
          <Descriptions.Item label="Subject">
            {detail.subjectName || (
              <Tag color="cyan">Preschool (no subject)</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Academic Year">
            {detail.academicYear}
          </Descriptions.Item>
          <Descriptions.Item label="Term">
            {detail.term || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag
              color={
                detail.status === "Active"
                  ? "green"
                  : detail.status === "Inactive"
                    ? "gold"
                    : "default"
              }
            >
              {detail.status || "-"}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Uploaded By">
            {detail.uploadedBy || "-"}
          </Descriptions.Item>
        </Descriptions>

        <Divider orientation="left">Files</Divider>
        {detail.files?.length ? (
          <List
            size="small"
            dataSource={detail.files}
            renderItem={(file) => (
              <List.Item
                actions={[
                  <Button
                    type="text"
                    size="small"
                    icon={<DownloadOutlined />}
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  />,
                  <Popconfirm
                    title="Remove this file?"
                    onConfirm={() => handleDeleteFile(file.id)}
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      loading={deletingFileId === file.id}
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>,
                ]}
              >
                <Space>
                  <PaperClipOutlined />
                  <Text>{file.filename}</Text>
                </Space>
              </List.Item>
            )}
          />
        ) : (
          <Text type="secondary">No files attached</Text>
        )}

        <Divider orientation="left">Upload More Files</Divider>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Select Files</Button>
          </Upload>
          {newFileList.length > 0 && (
            <Button
              type="primary"
              loading={uploadingFiles}
              onClick={handleUploadMore}
            >
              Upload {newFileList.length} file
              {newFileList.length > 1 ? "s" : ""}
            </Button>
          )}
        </Space>
      </>
    );
  };

  return (
    <Drawer
      title={
        <Title level={5} style={{ margin: 0 }}>
          Holiday Package Details
        </Title>
      }
      open={visible}
      onClose={onClose}
      width={600}
      destroyOnClose
    >
      {renderContent()}
    </Drawer>
  );
};

export default HolidayPackageDetailDrawer;
